import React, { FC, useState } from 'react';
import {
  Box,
  Icon,
  IconSize,
  Text,
  ToggleSwitch,
  Button,
  ButtonAppearance,
  ToastType,
  useToastsDispatch,
  SpinnerSize,
  Spinner,
  theme,
  ButtonSize,
} from '@bechtel/gds';
import { TextWithEllipsis, ModalError, ErrorModal, FormField, ErrorBoundary } from '@bechtel/client-common';
import { DeleteProjectReportModal } from '../delete';
import { get } from 'lodash';
import {
  UpdateProjectReportError,
  UpdateProjectReportInput,
  UpdateProjectReportPayload,
  DeleteProjectReportError,
  DeleteProjectReportPayload,
  DeleteProjectReportInput,
} from '@bechtel/types';
import { UPDATE_PROJECT_REPORT, DELETE_PROJECT_REPORT } from '@bechtel/client-gql';
import { useMutation } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
import { object, string } from 'yup';
import { ErrorMessage, Formik } from 'formik';
import { EditBarStyled, EditItemStyled, ItemStyled, EditReportContainerStyled } from './styled';
import { ReportItemProps } from './types';
import { getError } from '../error';
import { useLingui } from '@lingui/react';
import { clearCacheByKey, QueryKeys } from '~/services/cache';

const updateReportToast = (reportName: string) => {
  return (
    <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`The Report ${reportName} has been succesfully updated.`}
      </Text>
    </Box>
  );
};

export const FieldError = ({ msg }) => {
  const { i18n } = useLingui();
  return (
    <Box pt={{ sm: '400' }} pb={{ sm: '500' }}>
      <Text color="bechtel.red">{i18n._(msg)}</Text>
    </Box>
  );
};

const deleteReportToast = (reportName: string) => {
  return (
    <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`The Report ${reportName} has been succesfully deleted.`}
      </Text>
    </Box>
  );
};
const urlRegex = /^https:\/\/app\.powerbi\.com/;
const updateReportValidationSchema = object().shape({
  name: string()
    .trim()
    .min(5, t`Please enter atleast one character for name!`)
    .max(100, t`Too Long!`)
    .required(t`Name is mandatory`),
  description: string()
    .trim()
    .min(5, t`Please enter atleast one character for description!`)
    .max(2000, t`Too Long!`),
  powerbiUrl: string()
    .matches(urlRegex, t`Enter correct url!`)
    .required(t`Power BI URL is mandatory`),
});

export const ReportItem: FC<ReportItemProps> = ({
  reportItem,
  setLoadReportList,
  setDisableAddReport,
  disableEditReport,
}) => {
  const { i18n } = useLingui();
  const [isReportEdit, setIsReportEdit] = useState(false);
  const [updateProjectReportError, setUpdateProjectReportError] = useState<ModalError<UpdateProjectReportError>>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteProjectReportError, setDeleteProjectReportError] = useState<ModalError<DeleteProjectReportError>>(null);
  const dispatchToast = useToastsDispatch();
  const decodedPowerBiUrl = decodeURIComponent(reportItem.powerbiUrl).replace(/%27/g, "'");
  const [updateProjectReportData, { loading }] = useMutation<
    { updateProjectReport: UpdateProjectReportPayload },
    { input: UpdateProjectReportInput }
  >(UPDATE_PROJECT_REPORT, {
    onCompleted: (data) => {
      const updateProjectReportError = get(data, 'updateProjectReport.error');
      if (updateProjectReportError) {
        setUpdateProjectReportError(updateProjectReportError);
      } else {
        setIsReportEdit(false);
        setDisableAddReport(false);
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => updateReportToast(reportItem.name),
            autoDismissTimeout: 5000,
          },
        });
      }
    },
    onError: (error) => {
      setUpdateProjectReportError(error);
    },
  });

  //Delete single report
  const [deleteProjectReport, { loading: deleteLoading }] = useMutation<
    { deleteProjectReport: DeleteProjectReportPayload },
    { input: DeleteProjectReportInput }
  >(DELETE_PROJECT_REPORT, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const deleteProjectReportError = get(data, 'deleteProjectReport.error');
      setShowDeleteConfirm(false);
      if (deleteProjectReportError) {
        setDeleteProjectReportError(deleteProjectReportError);
      } else {
        setDeleteProjectReportError(null);
        setLoadReportList(true);
        setDisableAddReport(false);
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: () => deleteReportToast(reportItem.name),
            autoDismissTimeout: 5000,
          },
        });
      }
    },
    onError: (error) => {
      setDeleteProjectReportError(error);
    },
    update: (cache, result) => {
      if (!result.data?.deleteProjectReport.error) {
        clearCacheByKey(cache, QueryKeys.REPORTS);
      }
    },
  });

  const onDeleteProjectReport = () => {
    deleteProjectReport({
      variables: {
        input: {
          id: reportItem.id,
        },
      },
    });
    setLoadReportList(false);
  };

  return (
    <>
      {isReportEdit ? (
        <EditItemStyled data-testid={`data-report-item-edit-${reportItem.id}`} mt={{ sm: '600' }}>
          <Formik
            enableReinitialize={true}
            validationSchema={updateReportValidationSchema}
            initialValues={{
              name: reportItem?.name,
              description: reportItem?.description,
              powerbiUrl: decodedPowerBiUrl,
              isActive: reportItem?.isActive,
            }}
            onSubmit={(values) => {
              const encodePowerbiUrl = encodeURIComponent(values.powerbiUrl).replace(/'/g, '%27');
              updateProjectReportData({
                variables: {
                  input: {
                    name: values.name.trim(),
                    description: values.description.trim(),
                    powerbiUrl: encodePowerbiUrl,
                    isActive: values.isActive,
                    id: reportItem.id,
                  },
                },
              });
            }}
          >
            {({ setFieldValue, values, dirty, isValid, handleSubmit }) => {
              return (
                <>
                  <EditReportContainerStyled>
                    <Box mb={{ sm: '550' }} ml={{ sm: '400' }}>
                      <Text family="graphik" size="500" weight="600">
                        <Trans>Edit Report</Trans>
                      </Text>
                    </Box>
                    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                      <FormField labelName={t`Name`} fieldName="name" placeholder={t`Enter Name`} />
                      <ErrorMessage name="name">
                        {(msg) => {
                          return <FieldError msg={i18n._(msg)} />;
                        }}
                      </ErrorMessage>
                    </Box>
                    <Box pb={{ sm: '450' }} pt={{ sm: '550' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                      <FormField
                        labelName={t`Description`}
                        fieldName="description"
                        placeholder={t`Enter Description`}
                      />
                      <ErrorMessage name="description">
                        {(msg) => {
                          return <FieldError msg={i18n._(msg)} />;
                        }}
                      </ErrorMessage>
                    </Box>

                    <Box pb={{ sm: '450' }} pt={{ sm: '550' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                      <FormField
                        labelName={t`Power BI URL`}
                        fieldName="powerbiUrl"
                        placeholder={t`Enter Power BI URL`}
                      />
                      <ErrorMessage name="powerbiUrl">
                        {(msg) => {
                          return <FieldError msg={i18n._(msg)} />;
                        }}
                      </ErrorMessage>
                    </Box>
                    <Box ml={{ sm: '450' }}>
                      <ToggleSwitch
                        isToggled={values.isActive ? true : false}
                        onToggle={() => setFieldValue('isActive', !values.isActive)}
                      />
                    </Box>
                    <Box>
                      <Button
                        appearance={ButtonAppearance.TERTIARY}
                        disabled={loading}
                        onClick={() => {
                          setIsReportEdit(false);
                          setDisableAddReport(false);
                        }}
                      >
                        <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                          <Trans>Cancel</Trans>
                        </Text>
                      </Button>
                      <Button
                        appearance={ButtonAppearance.TERTIARY}
                        disabled={!dirty || !isValid}
                        onClick={() => handleSubmit()}
                      >
                        {loading ? (
                          <Spinner size={SpinnerSize.SMALL} />
                        ) : (
                          <Text family="graphikCompact" weight="600" size="400" color="bechtel.blue.800">
                            <Trans>Save</Trans>
                          </Text>
                        )}
                      </Button>
                    </Box>
                  </EditReportContainerStyled>
                </>
              );
            }}
          </Formik>
        </EditItemStyled>
      ) : (
        <ItemStyled
          data-testid={`data-report-item-${reportItem.id}`}
          justifyContent={{ sm: 'space-between' }}
          alignItems={{ sm: 'center' }}
          px={{ sm: '550' }}
          pb={{ sm: '550' }}
          pt={{ sm: '500' }}
          mt={{ sm: '400' }}
          isActive={reportItem.isActive}
        >
          <>
            <Box gap={{ sm: '450' }} style={{ height: '100%' }}>
              <EditBarStyled />
              <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} mb={{ sm: '300' }}>
                <Text family="graphik" color="grey.1000" size="500" style={{ marginBottom: theme.spacings[300] }}>
                  {reportItem.name}
                </Text>
                <TextWithEllipsis
                  family="graphik"
                  color="grey.600"
                  size="300"
                  title={reportItem.description}
                  style={{ wordBreak: 'break-word', whiteSpace: 'pre-line', marginBottom: theme.spacings[300] }}
                >
                  {reportItem.description}
                </TextWithEllipsis>

                <TextWithEllipsis
                  family="graphik"
                  color="grey.600"
                  size="300"
                  title={decodedPowerBiUrl}
                  style={{ wordBreak: 'break-word', whiteSpace: 'pre-line', marginBottom: theme.spacings[300] }}
                >
                  {decodedPowerBiUrl}
                </TextWithEllipsis>
              </Box>
            </Box>
            <Box alignItems={{ sm: 'center' }}>
              <Box>
                <Button
                  size={ButtonSize.SMALL}
                  appearance={ButtonAppearance.SECONDARY}
                  disabled={disableEditReport}
                  onClick={() => {
                    setIsReportEdit(true);
                    setDisableAddReport(true);
                  }}
                >
                  <Icon
                    icon="edit"
                    aria-disabled
                    size={IconSize.LARGE}
                    fillColor="blue.800"
                    style={{ cursor: 'pointer' }}
                  />
                </Button>
              </Box>
              <Box ml={{ sm: '600' }} mr={{ sm: '600' }}>
                <ToggleSwitch isToggled={reportItem.isActive} disabled={true} />
              </Box>
              <Box>
                <Icon
                  icon="trash-can"
                  size={IconSize.LARGE}
                  fillColor="red.500"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowDeleteConfirm(true)}
                />
              </Box>
            </Box>
          </>
        </ItemStyled>
      )}
      {updateProjectReportError && (
        <ErrorBoundary>
          <ErrorModal
            title={t`Update Report Error`}
            error={getError(updateProjectReportError)}
            close={() => setUpdateProjectReportError(null)}
          />
        </ErrorBoundary>
      )}
      {deleteProjectReportError && (
        <ErrorBoundary>
          <ErrorModal
            title={t`Delete Report Error`}
            error={getError(deleteProjectReportError)}
            close={() => setDeleteProjectReportError(null)}
          />
        </ErrorBoundary>
      )}
      {showDeleteConfirm && (
        <DeleteProjectReportModal
          onDelete={() => onDeleteProjectReport()}
          onCancel={() => setShowDeleteConfirm(false)}
          title={t`Delete Report`}
          textContent={t`Are you sure you want to delete the Report ${reportItem.name} ?`}
        ></DeleteProjectReportModal>
      )}
    </>
  );
};
